import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SpinnerIcon, AlertIcon, SuccessIcon, UncheckedIcon } from "@/lib/icons";
import { Progress } from "@/components/ui/progress";
import { ProjectColumnEditor } from "./ProjectColumnEditor";
import { useProjectInitData, type SchedulingTaskForImport, type TemplateProcurementPackage, type TemplateTaskAvailabilityMaps, type TemplateWorkSection, type SchedulingSpace } from "./hooks/useProjectInitData";
import { useProjectInitialisationData } from "./hooks/useProjectInitialisationData";
import { useInitialiseProject, useInitialisationProgress, useCancelInitialization } from "./hooks/useInitialiseProject";
import { toFinnishLabel } from "@/lib/initStepLabels";
import type { Step1FormData } from "./ProjectInitialisationDialog";
import type { PlotState, ProcurementPackageState, WorkSectionState, SpaceState } from "./hooks/useProjectInitData";

// The init breakdown flow does not persist section-location responsibles, so the
// responsible column has no DB-location mapping to assign against. A stable empty
// map keeps the column inert here while satisfying the required prop.
const EMPTY_SPACE_ID_TO_LOCATION_ID: Map<string, string> = new Map();

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  step1Data: Step1FormData;
  onCreated?: () => void;
}

/**
 * Find the PLOT/TONTTI ancestor for a given scheduling space by walking
 * up the parentId chain.  Returns the plot space's id or null if orphaned.
 */
function getPlotIdForSpace(
  spaceId: string,
  spaceById: Map<string, SchedulingSpace>,
  plotIds: Set<string>,
): string | null {
  let current = spaceId;
  const visited = new Set<string>();
  while (current) {
    if (plotIds.has(current)) return current;
    if (visited.has(current)) return null; // circular ref guard
    visited.add(current);
    const space = spaceById.get(current);
    if (!space?.parentId) return null;
    current = space.parentId;
  }
  return null;
}

/**
 * Build work sections for a given set of tasks grouped by section code,
 * returning a map of templatePP-id → WorkSectionState[].
 */
function buildWorkSectionsForTasks(
  tasksBySectionCode: Map<string, SchedulingTaskForImport[]>,
  templatePackageByCode: Map<string, TemplateProcurementPackage>,
  workSectionCodeToId: Map<string, string>,
  availableAtWs: Map<string, Set<string>>,
): Map<string, WorkSectionState[]> {
  const packageWorkSectionsMap = new Map<string, WorkSectionState[]>();

  for (const [sectionCode, sectionTasks] of tasksBySectionCode) {
    const schedulingPpCode = sectionTasks[0]?.procurementPackageCode ?? null;
    const matchedPkg = schedulingPpCode
      ? templatePackageByCode.get(schedulingPpCode)
      : undefined;
    const pkgId = matchedPkg?.id || "unassigned";

    const orderedTasks = sectionTasks
      .map((t, i) => ({ t, i }))
      .sort((a, b) => {
        const sa = a.t.plannedStart || "";
        const sb = b.t.plannedStart || "";
        return sa === sb ? a.i - b.i : sa < sb ? -1 : 1;
      });
    const firstIdx = orderedTasks[0]?.i;
    const lastIdx = orderedTasks[orderedTasks.length - 1]?.i;

    // Task names available for this work section in the selected template set.
    // The default flags below are gated on this so we never seed (and later
    // submit) an inspection/document flag for a task that doesn't exist here —
    // those are greyed out in the UI and the backend can't create them.
    const wsId = workSectionCodeToId.get(sectionCode);
    const wsAvail =
      (wsId ? availableAtWs.get(wsId) : undefined) ?? new Set<string>();

    const spaceStates: SpaceState[] = sectionTasks.map((task, idx) => ({
      schedulingSpaceId: task.spaceId,
      schedulingTaskIds: task.schedulingTaskIds,
      plannedStart: task.plannedStart,
      plannedEnd: task.plannedEnd,
      includeOsakohteenTarkastus: wsAvail.has("Osakohteen tarkastus"),
      includeMalliasennustarkastus:
        idx === firstIdx && wsAvail.has("Malliasennustarkastus"),
      includeMestanVastaanotto:
        idx === firstIdx && wsAvail.has("Mestan vastaanotto"),
      includeTyovaiheenvastaanotto:
        idx === lastIdx && wsAvail.has("Työvaiheenvastaanotto"),
      includeLiitedokumentit: idx === firstIdx && wsAvail.has("Liitedokumentit"),
    }));

    console.log(
      `[WS-DEBUG] sectionCode=${sectionCode}`,
      'spaceStates:',
      spaceStates.map(s => ({ spaceId: s.schedulingSpaceId, plannedStart: s.plannedStart, plannedEnd: s.plannedEnd })),
    );

    const dates = spaceStates
      .map(s => ({ start: s.plannedStart, end: s.plannedEnd }))
      .filter(d => d.start && d.end);

    console.log(
      `[WS-DEBUG] sectionCode=${sectionCode}`,
      'filtered dates array:',
      dates,
    );

    const plannedStart = dates.length > 0
      ? dates.reduce((earliest, d) => d.start! < earliest ? d.start! : earliest, dates[0].start!)
      : null;

    const plannedEnd = dates.length > 0
      ? dates.reduce((latest, d) => d.end! > latest ? d.end! : latest, dates[0].end!)
      : null;

    console.log(
      `[WS-DEBUG] sectionCode=${sectionCode}`,
      'RESULT: plannedStart=', plannedStart, 'plannedEnd=', plannedEnd,
    );

    const workSection: WorkSectionState = {
      sectionCode,
      sectionName: sectionTasks[0]?.sectionName || sectionCode,
      templateWorkSectionId: workSectionCodeToId.get(sectionCode),
      spaces: spaceStates,
      plannedStart,
      plannedEnd,
      inspectorUserId: null,
      spaceResponsibles: {},
    };

    const existing = packageWorkSectionsMap.get(pkgId) || [];
    existing.push(workSection);
    packageWorkSectionsMap.set(pkgId, existing);
  }

  return packageWorkSectionsMap;
}

/**
 * Convert a packageWorkSectionsMap into ProcurementPackageState[].
 */
function buildProcurementPackageStates(
  packageWorkSectionsMap: Map<string, WorkSectionState[]>,
  templatePackages: TemplateProcurementPackage[],
  defaultResponsibleUserId: string | null,
  refDatesByPpCode: Map<string, string[]>,
): ProcurementPackageState[] {
  const result: ProcurementPackageState[] = [];

  for (const pkg of templatePackages) {
    const workSections = packageWorkSectionsMap.get(pkg.id) || [];
    const refDates = refDatesByPpCode.get(pkg.code) ?? [];
    // A PP with only (HP) references (no work sections) is still created.
    if (workSections.length === 0 && refDates.length === 0) continue;

    const allWsDates = workSections.flatMap(ws => [
      ws.plannedStart,
      ws.plannedEnd
    ].filter((d): d is string => d != null));
    const allDates = [...allWsDates, ...refDates];

    const plannedStart = allDates.length > 0
      ? allDates.reduce((a, b) => a < b ? a : b)
      : null;
    const plannedEnd = allDates.length > 0
      ? allDates.reduce((a, b) => a > b ? a : b)
      : null;

    result.push({
      templateProcurementPackageId: pkg.id,
      code: pkg.code,
      name: pkg.name,
      responsibleUserId: defaultResponsibleUserId,
      includeSuunnitelmiin: true,
      includeAliurakka: true,
      includeAloitusedellytykset: true,
      includeTaloudellinenLoppuselvitys: true,
      workSections,
      plannedStart,
      plannedEnd,
    });
  }

  const unassignedSections = packageWorkSectionsMap.get("unassigned");
  if (unassignedSections && unassignedSections.length > 0) {
    const allUnassignedDates = unassignedSections.flatMap(ws => [
      ws.plannedStart,
      ws.plannedEnd
    ].filter((d): d is string => d != null));

    const unassignedStart = allUnassignedDates.length > 0
      ? allUnassignedDates.reduce((a, b) => a < b ? a : b)
      : null;
    const unassignedEnd = allUnassignedDates.length > 0
      ? allUnassignedDates.reduce((a, b) => a > b ? a : b)
      : null;

    result.push({
      templateProcurementPackageId: "unassigned",
      code: "",
      name: "Määrittämättömät työvaiheet",
      responsibleUserId: defaultResponsibleUserId,
      includeSuunnitelmiin: false,
      includeAliurakka: false,
      includeAloitusedellytykset: false,
      includeTaloudellinenLoppuselvitys: false,
      workSections: unassignedSections,
      plannedStart: unassignedStart,
      plannedEnd: unassignedEnd,
    });
  }

  return result;
}

/**
 * Build initial plot structure from scheduling data and template procurement packages.
 *
 * Maps sitedrive work sections to template procurement packages by work section code.
 * Each plot receives ONLY the tasks/spaces that belong to it (determined by walking
 * the parentId chain up to a PLOT/TONTTI space).
 */
function buildInitialPlots(
  tasks: SchedulingTaskForImport[],
  spaces: SchedulingSpace[],
  procurementPackages: TemplateProcurementPackage[],
  templateWorkSections: TemplateWorkSection[],
  availability: TemplateTaskAvailabilityMaps,
  ppReferenceTasks: SchedulingTaskForImport[],
  defaultResponsibleUserId?: string | null
): PlotState[] {
  const plotsToCreate = spaces
    .filter(s => s.type === "PLOT" || s.type === "TONTTI")
    .sort((a, b) => (a.orderNumber ?? 0) - (b.orderNumber ?? 0));
  if (plotsToCreate.length === 0) return [];

  const templatePackageByCode = new Map<string, TemplateProcurementPackage>();
  for (const pkg of procurementPackages) {
    templatePackageByCode.set(pkg.code, pkg);
  }

  const workSectionCodeToId = new Map<string, string>();
  for (const ws of templateWorkSections) {
    workSectionCodeToId.set(ws.code, ws.id);
  }

  // Build lookup structures for space→plot resolution
  const spaceById = new Map<string, SchedulingSpace>();
  for (const s of spaces) {
    spaceById.set(s.id, s);
  }
  const plotIds = new Set(plotsToCreate.map(p => p.id));

  // (HP) reference dates, grouped plot → ppCode → ISO date strings. These feed
  // a PP's min/max alongside its work-section dates but never become sections.
  const refDatesByPlotAndPp = new Map<string, Map<string, string[]>>();
  for (const ref of ppReferenceTasks) {
    const plotId = getPlotIdForSpace(ref.spaceId, spaceById, plotIds);
    if (!plotId || !ref.procurementPackageCode) continue;
    if (!refDatesByPlotAndPp.has(plotId)) refDatesByPlotAndPp.set(plotId, new Map());
    const byPp = refDatesByPlotAndPp.get(plotId)!;
    const dates = byPp.get(ref.procurementPackageCode) ?? [];
    if (ref.plannedStart) dates.push(ref.plannedStart);
    if (ref.plannedEnd) dates.push(ref.plannedEnd);
    byPp.set(ref.procurementPackageCode, dates);
  }

  // Group tasks by plot, then by section code within each plot
  const tasksByPlotAndSection = new Map<string, Map<string, SchedulingTaskForImport[]>>();
  // Tasks whose space cannot be traced to any plot
  const orphanedTasks = new Map<string, SchedulingTaskForImport[]>();

  for (const task of tasks) {
    const plotId = getPlotIdForSpace(task.spaceId, spaceById, plotIds);
    if (!plotId) {
      // Orphaned — cannot determine plot; add to fallback
      const existing = orphanedTasks.get(task.sectionCode) || [];
      existing.push(task);
      orphanedTasks.set(task.sectionCode, existing);
      continue;
    }

    if (!tasksByPlotAndSection.has(plotId)) {
      tasksByPlotAndSection.set(plotId, new Map());
    }
    const plotSections = tasksByPlotAndSection.get(plotId)!;
    const existing = plotSections.get(task.sectionCode) || [];
    existing.push(task);
    plotSections.set(task.sectionCode, existing);
  }

  // Build each plot with its own procurement packages
  const plots: PlotState[] = plotsToCreate.map((plotSpace) => {
    const plotTasksBySectionCode = tasksByPlotAndSection.get(plotSpace.id) || new Map();

    const packageWorkSectionsMap = buildWorkSectionsForTasks(
      plotTasksBySectionCode,
      templatePackageByCode,
      workSectionCodeToId,
      availability.availableAtWs,
    );

    const plotProcurementPackages = buildProcurementPackageStates(
      packageWorkSectionsMap,
      procurementPackages,
      defaultResponsibleUserId ?? null,
      refDatesByPlotAndPp.get(plotSpace.id) ?? new Map(),
    );

    return {
      name: plotSpace.name,
      locationSchedulingSpaceId: plotSpace.id,
      procurementPackages: plotProcurementPackages,
    };
  });

  // If there are orphaned tasks (spaces that couldn't be traced to a plot),
  // distribute them to the first plot as a fallback so they aren't lost.
  if (orphanedTasks.size > 0 && plots.length > 0) {
    const orphanedWsMap = buildWorkSectionsForTasks(
      orphanedTasks,
      templatePackageByCode,
      workSectionCodeToId,
      availability.availableAtWs,
    );

    const orphanedPPs = buildProcurementPackageStates(
      orphanedWsMap,
      procurementPackages,
      defaultResponsibleUserId ?? null,
      new Map(),
    );

    // Merge orphaned PPs into first plot: append work sections to matching
    // PPs, or add new PPs if they don't exist yet.
    const firstPlot = plots[0];
    const existingByTemplate = new Map(
      firstPlot.procurementPackages.map(pp => [pp.templateProcurementPackageId, pp]),
    );

    for (const orphanPP of orphanedPPs) {
      const existing = existingByTemplate.get(orphanPP.templateProcurementPackageId);
      if (existing) {
        existing.workSections.push(...orphanPP.workSections);
      } else {
        firstPlot.procurementPackages.push(orphanPP);
      }
    }
  }

  return plots;
}

export function ProjectInitialisationWorkSectionBreakdownDialog({
  open,
  onOpenChange,
  step1Data,
  onCreated,
}: Props) {
  const {
    tasks,
    spaces,
    procurementPackages,
    templateWorkSections,
    templateTaskAvailability,
    procurementPackageReferenceTasks,
    loading: dataLoading,
    error: dataError,
  } = useProjectInitData(
    open ? step1Data.schedulingProjectId : null,
    open ? step1Data.taskTemplateSetId : null
  );

  const { users } = useProjectInitialisationData();

  const [plots, setPlots] = useState<PlotState[]>([]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Track whether the user has edited any of the auto-built defaults so the
  // close-guard knows whether to confirm lost work.
  const isDirtyRef = useRef(false);

  // Separate polling state from Apollo's loading — we control this manually
  // so that it is set BEFORE the mutation call and cleared AFTER completion
  const [isPolling, setIsPolling] = useState(false);
  const [operationId, setOperationId] = useState<string | null>(null);
  const operationIdRef = useRef<string | null>(null);

  const { initialiseProject, loading: submitting } = useInitialiseProject();
  const { cancelInitialization } = useCancelInitialization();

  const { progress, isComplete, workerError, reset: resetProgress } = useInitialisationProgress(
    operationId,
    isPolling,
    () => {
      // onComplete callback — worker finished successfully.  Show the green
      // terminal state for a moment so the user sees confirmation, then close.
      setIsPolling(false);
      onCreated?.();
      setTimeout(() => {
        onOpenChange(false);
      }, 2500);
    },
    (error) => {
      // onError callback - worker failed
      setSubmitError(error);
      setIsPolling(false);
    },
  );

  // Initialise plots when data loads. Wait for !dataLoading so the template-task
  // availability query has resolved — otherwise the availability-gated default
  // flags would all seed false.
  useEffect(() => {
    if (!dataLoading && tasks.length > 0 && procurementPackages.length > 0 && templateWorkSections.length > 0 && plots.length === 0) {
      setPlots(
        buildInitialPlots(
          tasks,
          spaces,
          procurementPackages,
          templateWorkSections,
          templateTaskAvailability,
          procurementPackageReferenceTasks,
          step1Data.vastaavaTyonjohtajaUserId,
        ),
      );
    }
  }, [
    dataLoading,
    tasks,
    procurementPackages,
    templateWorkSections,
    templateTaskAvailability,
    procurementPackageReferenceTasks,
    spaces,
    plots.length,
    step1Data.vastaavaTyonjohtajaUserId,
  ]);

  // Reset when closed
  useEffect(() => {
    if (!open) {
      setPlots([]);
      setSubmitError(null);
      setIsPolling(false);
      setOperationId(null);
      operationIdRef.current = null;
      isDirtyRef.current = false;
      resetProgress();
    }
  }, [open, resetProgress]);

  // Calculate validation state
  // Sitedrive must expose at least one PLOT/TONTTI space per project.
  // Show blocking error if not — operator needs to fix scheduling data.
  const missingPlotSpaces = useMemo(
    () => !dataLoading && !dataError && spaces.length > 0
      && !spaces.some(s => s.type === "PLOT" || s.type === "TONTTI"),
    [dataLoading, dataError, spaces],
  );

  const allResponsibleSelected = useMemo(() => {
    return plots.every(plot => 
      plot.procurementPackages.every(pp => pp.responsibleUserId !== null)
    );
  }, [plots]);

  const missingCount = useMemo(() => {
    let count = 0;
    for (const plot of plots) {
      for (const pp of plot.procurementPackages) {
        if (!pp.responsibleUserId) count++;
      }
    }
    return count;
  }, [plots]);

  const totalPackages = useMemo(() => {
    return plots.reduce((sum, plot) => sum + plot.procurementPackages.length, 0);
  }, [plots]);

  const handleUpdatePlot = (plotIndex: number, updated: PlotState) => {
    isDirtyRef.current = true;
    setPlots((prev) => prev.map((p, i) => (i === plotIndex ? updated : p)));
  };

  /** Wrap the caller's onOpenChange so accidental closes don't wipe filled-in
   *  step-2 state.  Once a job is running we never confirm — the toast already
   *  carries progress and reopening the dialog starts fresh. */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next && isDirtyRef.current && !submitting && !isPolling && !isComplete) {
        const ok = window.confirm(
          "Sulje ilman tallentamista? Tekemäsi valinnat menetetään.",
        );
        if (!ok) return;
      }
      onOpenChange(next);
    },
    [onOpenChange, submitting, isPolling, isComplete],
  );

  const handleSubmit = async () => {
    if (!allResponsibleSelected) return;
    setSubmitError(null);
    resetProgress();

    // Generate operation ID and start polling BEFORE calling the mutation
    const opId = crypto.randomUUID();
    operationIdRef.current = opId;
    setOperationId(opId);
    setIsPolling(true);

    try {
      // Transform plots data to match backend GraphQL input
      const plotsInput = plots.map((plot) => ({
        name: plot.name,
        locationSchedulingSpaceId: plot.locationSchedulingSpaceId,
        procurementPackages: plot.procurementPackages.map((pp) => ({
          templateProcurementPackageId: pp.templateProcurementPackageId,
          responsibleUserId: pp.responsibleUserId!,
          code: pp.code,
          name: pp.name,
          includeSuunnitelmiinPerehtyminen: pp.includeSuunnitelmiin,
          includeAliurakkaPerehtyminen: pp.includeAliurakka,
          includeAloitusedellytykset: pp.includeAloitusedellytykset,
          includeTaloudellinenLoppuselvitys: pp.includeTaloudellinenLoppuselvitys,
          plannedStart: pp.plannedStart,
          plannedEnd: pp.plannedEnd,
          workSections: pp.workSections.map((ws) => ({
            sectionCode: ws.sectionCode,
            sectionName: ws.sectionName,
            templateWorkSectionId: ws.templateWorkSectionId,
            plannedStart: ws.plannedStart,
            plannedEnd: ws.plannedEnd,
            responsibleUserId: ws.inspectorUserId,
            // Staged section-location responsibles, keyed by schedulingSpaceId
            // (incl. task-less ancestor rows). Worker resolves each to a Location.
            spaceResponsibles: Object.entries(ws.spaceResponsibles)
              .filter(([, v]) => v.responsibleUserId != null)
              .map(([schedulingSpaceId, v]) => ({
                schedulingSpaceId,
                responsibleUserId: v.responsibleUserId!,
              })),
            spaces: (() => {
              // Final guard: never submit a flag for a task not available in
              // this work section, regardless of how the state was set.
              const wsAvail =
                (ws.templateWorkSectionId
                  ? templateTaskAvailability.availableAtWs.get(
                      ws.templateWorkSectionId,
                    )
                  : undefined) ?? new Set<string>();
              return ws.spaces.map((s) => ({
                schedulingSpaceId: s.schedulingSpaceId,
                includeOsakohteenTarkastus:
                  s.includeOsakohteenTarkastus &&
                  wsAvail.has("Osakohteen tarkastus"),
                includeMalliasennustarkastus:
                  s.includeMalliasennustarkastus &&
                  wsAvail.has("Malliasennustarkastus"),
                includeMestanVastaanotto:
                  s.includeMestanVastaanotto && wsAvail.has("Mestan vastaanotto"),
                includeTyovaiheenvastaanotto:
                  s.includeTyovaiheenvastaanotto &&
                  wsAvail.has("Työvaiheenvastaanotto"),
                includeLiitedokumentit:
                  s.includeLiitedokumentit && wsAvail.has("Liitedokumentit"),
              }));
            })(),
          })),
        })),
      }));

      // DEBUG: Log spaces data to check for duplicates
      const spaceIds = spaces.map(s => s.id);
      const uniqueSpaceIds = [...new Set(spaceIds)];
      console.log("[DEBUG] Frontend spaces:", {
        total: spaces.length,
        unique: uniqueSpaceIds.length,
        allIds: spaceIds,
        duplicates: spaceIds.filter((id, index) => spaceIds.indexOf(id) !== index)
      });

      const allSpacesInput = spaces.map((s) => ({
        schedulingSpaceId: s.id,
        name: s.name,
        parentSchedulingSpaceId: s.parentId ?? undefined,
        spaceType: s.type ?? undefined,
      }));

      const result = await initialiseProject({
        name: step1Data.name,
        projectType: step1Data.projectType,
        taskTemplateSetId: step1Data.taskTemplateSetId!,
        schedulingProjectId: step1Data.schedulingProjectId!,
        tyopaallikkoUserId: step1Data.tyopaallikkoUserId!,
        vastaavaTyonjohtajaUserId: step1Data.vastaavaTyonjohtajaUserId!,
        description: step1Data.description || undefined,
        startDate: step1Data.startDate || undefined,
        objectiveEndDate: step1Data.objectiveEndDate || undefined,
        inspectionProjectOwnerId: step1Data.inspectionProjectOwnerId || undefined,
        existingInspectionProjectId: step1Data.existingInspectionProjectId || undefined,
        inspectionMatrixId: step1Data.inspectionMatrixId || undefined,
        inspectionTemplateOwnerProjectId: step1Data.inspectionTemplateOwnerProjectId || undefined,
        inspectionProjectCode: step1Data.inspectionProjectCode || undefined,
        operationId: opId,
        plots: plotsInput,
        allSpaces: allSpacesInput,
      });

      // Mutation returns immediately with operation_id; the global
      // BackgroundJobsToasts reads the server-side user→ops registry.
      if (!result?.success) {
        // Mutation itself failed (validation error, etc.)
        setSubmitError(result?.message ?? "Virhe taustatyön käynnistyksessä");
        setIsPolling(false);
      }
      // If mutation succeeded, polling continues until worker completes
    } catch (err: unknown) {
      setSubmitError(err instanceof Error ? err.message : "Virhe hankkeen luonnissa");
      setIsPolling(false);
    }
  };

  const handleStop = async () => {
    if (!operationIdRef.current) return;
    try {
      await cancelInitialization(operationIdRef.current);
    } catch {
      // Ignore errors - we close the dialog anyway
    }
    // Stop polling and close dialog immediately
    setIsPolling(false);
    onOpenChange(false);
  };

  const progressPercent = progress
    ? Math.round((progress.step / progress.totalSteps) * 100)
    : 0;

  const running = submitting || isPolling;
  const terminalState = isComplete || Boolean(submitError || workerError);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[1300px] h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>Alusta hanke — {step1Data.name}</DialogTitle>
          <DialogDescription className="text-sm">
            Valitse vastuuhenkilö ja tarkastukset hankintapaketeille. Jokainen
            tontti on oma projektinsa.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-hidden">
          {dataLoading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <SpinnerIcon className="h-8 w-8 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Ladataan aikataulu- ja pohjadataa...
              </p>
            </div>
          ) : dataError ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-destructive">
              <AlertIcon className="h-8 w-8" />
              <p className="text-sm font-medium">Virhe datan lataamisessa</p>
              <p className="text-xs text-muted-foreground max-w-md text-center">
                {dataError.message || "Tarkista yhteys Sitedriven API:in"}
              </p>
              <div className="text-xs text-muted-foreground">
                <p>Tasks: {tasks.length}</p>
                <p>Spaces: {spaces.length}</p>
                <p>Packages: {procurementPackages.length}</p>
                <p>Work Sections: {templateWorkSections.length}</p>
              </div>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => window.location.reload()}
                className="mt-2"
              >
                Yritä uudelleen
              </Button>
            </div>
          ) : missingPlotSpaces ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-destructive">
              <AlertIcon className="h-8 w-8" />
              <p className="text-sm font-medium">Projekti (PLOT/TONTTI) -tiloja ei löydy</p>
              <p className="text-xs text-muted-foreground max-w-md text-center">
                Sitedrive-hankkeessa ei ole yhtään PLOT- tai TONTTI-tyyppistä tilaa.
                Lisää vähintään yksi projektitila Sitedriveen ja yritä uudelleen.
              </p>
            </div>
          ) : plots.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3 text-muted-foreground">
              <p className="text-sm">Ei projekteja tai työvaiheita löydetty valitusta hankkeesta.</p>
            </div>
          ) : (
            <ProjectColumnEditor
              plots={plots}
              users={users}
              allSchedulingSpaces={spaces}
              templateTaskAvailability={templateTaskAvailability}
              onUpdatePlot={handleUpdatePlot}
              spaceIdToLocationId={EMPTY_SPACE_ID_TO_LOCATION_ID}
            />
          )}
        </div>

        {/* Single footer strip — form controls, live progress, or terminal
            status share the same real estate so the eye returns to one place. */}
        <DialogFooter className="shrink-0 flex-col items-stretch gap-3 sm:flex-col sm:space-x-0 border-t px-6 bg-background">
          {running || terminalState ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  {submitError || workerError ? (
                    <AlertIcon className="h-4 w-4 text-destructive shrink-0" />
                  ) : isComplete ? (
                    <SuccessIcon className="h-4 w-4 text-green-600 shrink-0" />
                  ) : (
                    <SpinnerIcon className="h-4 w-4 animate-spin text-muted-foreground shrink-0" />
                  )}
                  <span className="font-medium truncate">
                    {submitError || workerError
                      ? "Hankkeen luonti epäonnistui"
                      : isComplete
                      ? "Hanke luotu"
                      : progress?.stepLabel
                      ? toFinnishLabel(progress.stepLabel)
                      : "Käynnistetään taustatyötä..."}
                  </span>
                </div>
                <span className="text-muted-foreground tabular-nums shrink-0">
                  {progress && !terminalState
                    ? `Vaihe ${progress.step}/${progress.totalSteps}`
                    : ""}
                </span>
              </div>
              {!terminalState && (
                <Progress value={progressPercent} className="h-1.5" />
              )}
              {progress?.detail && !terminalState && (
                <p className="text-xs text-muted-foreground">{progress.detail}</p>
              )}
              {(submitError || workerError) && (
                <p className="text-xs text-destructive break-words whitespace-pre-wrap">
                  {submitError || workerError}
                </p>
              )}
              <div className="flex items-center justify-end gap-2 pt-1">
                {isPolling && !isComplete ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleStop}
                    className="h-8 px-3 text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
                  >
                    <UncheckedIcon className="h-3 w-3 mr-1.5 fill-current" />
                    Pysäytä
                  </Button>
                ) : terminalState ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenChange(false)}
                  >
                    Sulje
                  </Button>
                ) : null}
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              {plots.length > 0 && missingCount > 0 && (
                <div className="flex items-center gap-2 text-xs text-destructive flex-1">
                  <AlertIcon className="h-3.5 w-3.5 shrink-0" />
                  {missingCount === 1
                    ? `1/${totalPackages} hankintapaketilta puuttuu vastuuhenkilö`
                    : `${missingCount}/${totalPackages} hankintapaketilta puuttuu vastuuhenkilö`}
                </div>
              )}
              <div className="ml-auto flex items-center gap-2">
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  Peruuta
                </Button>
                <Button
                  onClick={handleSubmit}
                  disabled={!allResponsibleSelected || dataLoading || plots.length === 0}
                >
                  Luo hanke
                </Button>
              </div>
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
